"use client";

// Simple Tailwind UI Components - No custom CSS needed
// These examples show how to build UI using only Tailwind classes

import React, { useState } from 'react';

// === BASIC COMPONENTS ===

export function Button({ 
  children, 
  variant = 'primary', 
  size = 'md', 
  className = '', 
  ...props 
}: React.ComponentProps<'button'> & { 
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'wedding';
  size?: 'sm' | 'md' | 'lg';
}) {
  const baseClasses = 'inline-flex items-center justify-center rounded-lg font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50';
  
  const variants = {
    primary: 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5',
    secondary: 'bg-gradient-to-r from-gray-600 to-gray-700 text-white hover:from-gray-700 hover:to-gray-800 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5',
    outline: 'border-2 border-gray-300 bg-white text-gray-700 hover:border-blue-500 hover:text-blue-600 hover:shadow-md',
    ghost: 'text-gray-600 hover:text-blue-600 hover:bg-blue-50',
    wedding: 'border border-amber-300/70 bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-200 text-stone-900 shadow-[0_18px_36px_rgba(186,145,63,0.26)] hover:from-amber-300 hover:via-yellow-200 hover:to-amber-100 hover:shadow-[0_22px_40px_rgba(186,145,63,0.32)] transform hover:-translate-y-0.5'
  };
  
  const sizes = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-sm',
    lg: 'px-8 py-4 text-base'
  };
  
  return (
    <button 
      className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

export function Card({ children, className = '', ...props }: React.ComponentProps<'div'>) {
  return (
    <div 
      className={`bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-xl border border-white/20 hover:shadow-2xl hover:bg-white/90 transition-all duration-300 ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}

export function Container({ children, className = '', ...props }: React.ComponentProps<'div'>) {
  return (
    <div className={`max-w-7xl mx-auto px-6 lg:px-8 ${className}`} {...props}>
      {children}
    </div>
  );
}

export function Section({ children, size = 'md', className = '', ...props }: React.ComponentProps<'section'> & { size?: 'sm' | 'md' | 'lg' | 'xl' }) {
  const sizes = {
    sm: 'py-12',
    md: 'py-20',
    lg: 'py-32',
    xl: 'py-40'
  };
  
  return (
    <section className={`${sizes[size]} ${className}`} {...props}>
      {children}
    </section>
  );
}

export function Grid({ children, cols = 3, className = '', ...props }: React.ComponentProps<'div'> & { cols?: 1 | 2 | 3 | 4 }) {
  const gridCols = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
  };
  
  return (
    <div className={`grid ${gridCols[cols]} gap-8 ${className}`} {...props}>
      {children}
    </div>
  );
}

// === MODERN HEADER COMPONENT ===

interface ModernHeaderProps {
  logo?: string;
  navItems?: { label: string; href: string }[];
  cta?: { label: string; variant?: 'primary' | 'secondary' };
  theme?: 'light' | 'dark' | 'gradient';
  className?: string;
}

export function ModernHeader({ 
  logo = '🚀 Your Brand', 
  navItems = [],
  cta,
  theme = 'gradient',
  className = ''
}: ModernHeaderProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const themeStyles = {
    light: {
      header: 'bg-white/95 backdrop-blur-md border-b border-gray-200/50',
      logo: 'text-gray-900',
      nav: 'text-gray-600 hover:text-gray-900',
      burger: 'text-gray-600 hover:text-gray-900'
    },
    dark: {
      header: 'bg-gray-900/95 backdrop-blur-md border-b border-gray-700/50',
      logo: 'text-white',
      nav: 'text-gray-300 hover:text-white',
      burger: 'text-gray-300 hover:text-white'
    },
    gradient: {
      header: 'bg-gradient-to-r from-blue-600/95 via-purple-600/95 to-pink-600/95 backdrop-blur-md border-b border-white/20',
      logo: 'text-white',
      nav: 'text-white/80 hover:text-white',
      burger: 'text-white/80 hover:text-white'
    }
  };

  const currentTheme = themeStyles[theme];

  return (
    <>
      <header className={`sticky top-0 z-50 ${currentTheme.header} ${className}`}>
        <Container>
          <div className="flex justify-between items-center py-4">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              {/* <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <span className="text-xl">🎨</span>
              </div> */}
              <h1 className={`text-2xl font-bold tracking-tight ${currentTheme.logo}`}>
                {logo}
              </h1>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              {navItems.map((item, index) => (
                <a
                  key={index}
                  href={item.href}
                  className={`relative font-medium transition-colors duration-200 ${currentTheme.nav} group`}
                >
                  {item.label}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-300 group-hover:w-full"></span>
                </a>
              ))}
              {cta && (
                <Button 
                  variant={cta.variant || 'primary'} 
                  size="sm"
                  className="shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  {cta.label}
                </Button>
              )}
            </nav>
            
            {/* Mobile Burger Button */}
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden relative p-3 rounded-xl transition-all duration-200 ${currentTheme.burger} hover:bg-white/10`}
              aria-label="Toggle mobile menu"
            >
              <div className="w-6 h-5 relative flex flex-col justify-center space-y-1">
                <span className={`block w-full h-0.5 rounded-full transition-all duration-300 ${isMobileMenuOpen ? 'rotate-45 translate-y-1.5' : ''} ${theme === 'gradient' ? 'bg-white' : theme === 'dark' ? 'bg-white' : 'bg-gray-600'}`}></span>
                <span className={`block w-full h-0.5 rounded-full transition-all duration-300 ${isMobileMenuOpen ? 'opacity-0' : ''} ${theme === 'gradient' ? 'bg-white' : theme === 'dark' ? 'bg-white' : 'bg-gray-600'}`}></span>
                <span className={`block w-full h-0.5 rounded-full transition-all duration-300 ${isMobileMenuOpen ? '-rotate-45 -translate-y-1.5' : ''} ${theme === 'gradient' ? 'bg-white' : theme === 'dark' ? 'bg-white' : 'bg-gray-600'}`}></span>
              </div>
            </button>
          </div>
        </Container>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className={`lg:hidden fixed inset-0 z-40 ${theme === 'gradient' ? 'bg-gradient-to-br from-blue-600/95 via-purple-600/95 to-pink-600/95' : theme === 'dark' ? 'bg-gray-900/95' : 'bg-white/95'} backdrop-blur-md`}>
          <div className="flex flex-col justify-center items-center min-h-screen space-y-8 p-8">
            {navItems.map((item, index) => (
              <a
                key={index}
                href={item.href}
                className={`text-xl font-medium transition-colors duration-200 ${currentTheme.nav}`}
              >
                {item.label}
              </a>
            ))}
            {cta && (
              <Button 
                variant={cta.variant || 'primary'} 
                size="lg"
                className="w-full max-w-xs shadow-xl"
              >
                {cta.label}
              </Button>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// === EXAMPLE USAGE ===

export function ExamplePage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      {/* Modern Header */}
      <ModernHeader 
        logo="Modern UI"
        navItems={[
          { label: 'Features', href: '#features' },
          { label: 'Components', href: '#components' },
          { label: 'Documentation', href: '#docs' }
        ]}
        cta={{ label: 'Get Started', variant: 'primary' }}
        theme="gradient"
      />

      {/* Hero Section */}
      <Section size="lg">
        <Container>
          <div className="text-center max-w-5xl mx-auto space-y-8">
            <div className="space-y-4">
              <h1 className="text-5xl md:text-7xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 leading-tight">
                Next Generation
                <br />
                Tailwind UI
              </h1>
              <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Build stunning websites with pure Tailwind CSS. 
                <span className="font-semibold text-gray-900">No custom CSS required.</span>
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Button size="lg" className="shadow-2xl hover:shadow-3xl transform hover:-translate-y-1">
                Start Building
              </Button>
              <Button variant="outline" size="lg" className="border-2 hover:border-blue-500">
                View Examples
              </Button>
            </div>
          </div>
        </Container>
      </Section>

      {/* Features */}
      <Section size="lg" className="bg-white/50 backdrop-blur-sm">
        <Container>
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-4">
              Modern Features
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Everything you need for professional web development
            </p>
          </div>
          
          <Grid cols={3}>
            <Card className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl mx-auto mb-6 flex items-center justify-center text-white text-3xl group-hover:scale-110 transition-transform duration-300">
                ⚡
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Lightning Fast</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Optimized for performance with minimal CSS and maximum efficiency.
              </p>
              <Button variant="outline" className="w-full group-hover:border-blue-500 group-hover:text-blue-600">
                Learn More
              </Button>
            </Card>
            
            <Card className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl mx-auto mb-6 flex items-center justify-center text-white text-3xl group-hover:scale-110 transition-transform duration-300">
                🎨
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Beautiful Design</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Modern aesthetics with gradients, blur effects, and smooth animations.
              </p>
              <Button variant="outline" className="w-full group-hover:border-purple-500 group-hover:text-purple-600">
                Learn More
              </Button>
            </Card>
            
            <Card className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl mx-auto mb-6 flex items-center justify-center text-white text-3xl group-hover:scale-110 transition-transform duration-300">
                🔧
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Fully Customizable</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Easy to extend and customize for any brand or design system.
              </p>
              <Button variant="outline" className="w-full group-hover:border-green-500 group-hover:text-green-600">
                Learn More
              </Button>
            </Card>
          </Grid>
        </Container>
      </Section>

      {/* Footer */}
      <footer className="border-t border-gray-200/50 bg-white/80 backdrop-blur-sm">
        <Container>
          <div className="text-center py-12">
            <p className="text-gray-600 font-medium">Built with ❤️ using pure Tailwind CSS</p>
            <p className="text-sm text-gray-500 mt-2">Next.js 16 • TypeScript • Tailwind CSS v4 • Modern Design</p>
          </div>
        </Container>
      </footer>
    </div>
  );
}
